import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  TextField,
  Button,
  useTheme,
  useMediaQuery,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  Chip,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { useNavigate } from "react-router-dom";
import useIPAddress from "../Utils/useIPAddress";
import { baseapi } from "../Environmentalurl";
import {
  ApplicationType,
  entity_id,
  location,
  orderID,
  Platform,
} from "../Utils/Const";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DashboardHeader from "../Dashboard/DashboardHeader";
import api from "../Utils/Api";

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDT = (dt) => {
  if (!dt) return "-";
  const d = new Date(dt);
  const hours24 = d.getHours();
  const hours12 = hours24 % 12 || 12;
  const ampm = hours24 < 12 ? "AM" : "PM";
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()} ${String(hours12).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")} ${ampm}`;
};
const normStatus = (s = "") => s.toString().trim().toUpperCase();
const statusStyle = (s) => {
  const n = normStatus(s);
  if (n === "SUCCESS") return { bg: "#dcfce7", color: "#16a34a" };
  if (n === "FAILURE") return { bg: "#fee2e2", color: "#dc2626" };
  if (n === "PENDING") return { bg: "#fef3c7", color: "#d97706" };
  return { bg: "#f3f4f6", color: "#6b7280" };
};

const ROWS = 5;

// ── Sub-components ────────────────────────────────────────────────────────────
const DetailRow = ({ label, value }) => (
  <Box
    display="flex"
    justifyContent="space-between"
    alignItems="flex-start"
    sx={{
      py: 0.8,
      borderBottom: "1px dashed #f0f0f0",
      "&:last-child": { borderBottom: "none" },
    }}
  >
    <Typography fontSize={12} color="text.secondary" sx={{ minWidth: 100 }}>
      {label}
    </Typography>
    <Typography
      fontSize={13}
      fontWeight={500}
      textAlign="right"
      sx={{ maxWidth: 200, wordBreak: "break-word" }}
    >
      {value || "-"}
    </Typography>
  </Box>
);

const StatusBadge = ({ status, sx = {} }) => {
  const sc = statusStyle(status);
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        bgcolor: sc.bg,
        color: sc.color,
        px: 1.5,
        py: 0.4,
        borderRadius: 2,
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: "0.3px",
        ...sx,
      }}
    >
      {status || "-"}
    </Box>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function TransactionHistory() {
  const navigate = useNavigate();
  const ip = useIPAddress();

  const mobileNumber = JSON.parse(
    sessionStorage.getItem("mobileNumber") || "null",
  );
  const SessionID = JSON.parse(sessionStorage.getItem("SessionID") || "null");
  const userName =
    JSON.parse(sessionStorage.getItem("NameOnCard") || "null") || "";

  // Data state
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [searchText, setSearchText] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [applyFilter, setApplyFilter] = useState(false);
  const [page, setPage] = useState(1);

  // Dialog state
  const [selTxn, setSelTxn] = useState(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [openIssue, setOpenIssue] = useState(false);
  const [openFraud, setOpenFraud] = useState(false);

  // Report Issue state
  const [issueType, setIssueType] = useState("");
  const [issueDesc, setIssueDesc] = useState("");
  const [issueLoading, setIssueLoading] = useState(false);
  const [issueResult, setIssueResult] = useState(null);

  // ── File attachment for raise issue ─────────────────────────────────────
  const [selectedFile, setSelectedFile] = useState(null);
  const [filePreviewUrl, setFilePreviewUrl] = useState("");

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const allowed = ["image/png", "image/jpeg", "application/pdf"];
    if (!allowed.includes(file.type)) {
      alert("Only PNG, JPEG, or PDF files are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert("File size must not exceed 5MB");
      return;
    }
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setSelectedFile(file);
    setFilePreviewUrl(file.type === "application/pdf" ? "" : URL.createObjectURL(file));
  };

  const handleRemoveFile = () => {
    if (filePreviewUrl) URL.revokeObjectURL(filePreviewUrl);
    setSelectedFile(null);
    setFilePreviewUrl("");
  };

  // Report Fraud state
  const [fraudNature, setFraudNature] = useState("");
  const [fraudDate, setFraudDate] = useState("");
  const [fraudDesc, setFraudDesc] = useState("");
  const [fraudLoading, setFraudLoading] = useState(false);
  const [fraudResult, setFraudResult] = useState(null);
  const [fraudErr, setFraudErr] = useState("");

  // ── Fetch Transactions ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!ip) return;
    api
      .post(`${baseapi}/JanAadhaareWalletStatement`, {
        ReqType: "Jan_Aadhaar_eWallet_Statement",
        Mobilenumber: mobileNumber,
        ServiceID: "5",
        MachineID: ip,
        Platform,
        ApplicationType,
        GeoLocation: location,
        EntityID: entity_id,
        SessionID,
        FromDate: fromDate,
        ToDate: toDate,
      })
      .then((res) => {
        const rows =
          res.data?.Response_Code === "1000" && Array.isArray(res.data.Info)
            ? res.data.Info
            : [];
        setData(rows);

        // ── Client-side date range filter ──
        // The API may return all records; we re-filter on the frontend
        // so only transactions within the selected date range are shown.
        if (fromDate || toDate) {
          const from = fromDate ? new Date(fromDate) : null;
          const to = toDate ? new Date(toDate) : null;
          if (to) to.setHours(23, 59, 59, 999); // include the entire end day
          setFilteredData(
            rows.filter((r) => {
              const d = new Date(r.TrxnDate);
              if (from && d < from) return false;
              if (to && d > to) return false;
              return true;
            })
          );
        } else {
          setFilteredData(rows);
        }
      })
      .catch(console.error);
  }, [ip, applyFilter]);

  // ── Search ──────────────────────────────────────────────────────────────────
  const handleSearch = (e) => {
    const v = e.target.value;
    setSearchText(v);
    if (!v.trim()) {
      setFilteredData(data);
      return;
    }
    const lower = v.toLowerCase();
    setFilteredData(
      data.filter((r) => {
        const textMatch = [
          r.TransactionType || "",
          r.TransactionRemarks || "",
          r.TransactionID || "",
          r.OrderID || "",
        ].some((f) => f.toLowerCase().includes(lower));

        // match against formatted date string e.g. "14-06-2026 11:09 AM"
        const dateMatch = fmtDT(r.TrxnDate).toLowerCase().includes(lower);

        return textMatch || dateMatch;
      }),
    );
  };

  useEffect(() => {
    setPage(1);
  }, [searchText, fromDate, toDate, statusFilter]);

  // ── Derived data ────────────────────────────────────────────────────────────
  const statusFiltered =
    statusFilter === "ALL"
      ? filteredData
      : filteredData.filter((r) => normStatus(r.TrxnStatus) === statusFilter);
  const totalPages = Math.ceil(statusFiltered.length / ROWS);
  const paginated = statusFiltered.slice((page - 1) * ROWS, page * ROWS);
  const countOf = (s) =>
    filteredData.filter((r) => normStatus(r.TrxnStatus) === s).length;

  // ── Exports ─────────────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!statusFiltered.length) return;
    const ws = XLSX.utils.json_to_sheet(
      statusFiltered.map((r, i) => ({
        "S No": i + 1,
        Date: fmtDT(r.TrxnDate),
        "Transaction ID": r.TransactionID || "-",
        Description: r.TransactionRemarks || "-",
        Credit: +Number(r.Credit || 0).toFixed(2),
        Debit: +Number(r.Debit || 0).toFixed(2),
        Status: normStatus(r.TrxnStatus),
        Balance: +Number(r.Balance || 0).toFixed(2),
      })),
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    saveAs(
      new Blob([XLSX.write(wb, { bookType: "xlsx", type: "array" })], {
        type: "application/octet-stream",
      }),
      "TransactionHistory.xlsx",
    );
  };

  const exportPDF = () => {
    if (!statusFiltered.length) return;
    const doc = new jsPDF();
    autoTable(doc, {
      head: [
        [
          "S No",
          "Date",
          "Transaction ID",
          "Description",
          "Credit",
          "Debit",
          "Status",
          "Balance",
        ],
      ],
      body: statusFiltered.map((r, i) => [
        i + 1,
        fmtDT(r.TrxnDate),
        r.TransactionID || "-",
        r.TransactionRemarks || "-",
        Number(r.Credit || 0).toFixed(2),
        Number(r.Debit || 0).toFixed(2),
        normStatus(r.TrxnStatus),
        Number(r.Balance || 0).toFixed(2),
      ]),
      startY: 20,
      styles: { fontSize: 6, cellPadding: 1.5 },
      headStyles: { fillColor: [22, 163, 74], fontSize: 6, cellPadding: 2 },
    });
    doc.text("Transaction History", 14, 15);
    doc.save("TransactionHistory.pdf");
  };

  // ── Download Receipt ────────────────────────────────────────────────────────
  const downloadReceipt = () => {
    if (!selTxn) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    const safe = (v) => (v != null ? String(v) : "-");
    const img = new Image();
    img.src = "/Assets/tacircle_logo.png";
    img.onload = () => {
      doc.setFillColor(245, 247, 250);
      doc.roundedRect(10, 10, 190, 270, 6, 6, "F");
      doc.addImage(img, "PNG", pw / 2 - 9, 18, 18, 18);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(16);
      doc.text("Transaction Receipt", pw / 2, 45, { align: "center" });
      const isOk =
        safe(selTxn.TrxnStatus || "SUCCESS").toUpperCase() === "SUCCESS";
      doc.setFillColor(isOk ? 220 : 254, isOk ? 252 : 226, isOk ? 231 : 226);
      doc.roundedRect(pw / 2 - 25, 50, 50, 10, 3, 3, "F");
      doc.setFontSize(10);
      doc.setTextColor(isOk ? 22 : 220, isOk ? 163 : 38, isOk ? 74 : 38);
      doc.text(safe(selTxn.TrxnStatus), pw / 2, 57, { align: "center" });
      doc.setTextColor(0, 0, 0);
      doc.setDrawColor(220);
      doc.line(20, 70, 190, 70);
      let y = 80;
      const row = (l, v) => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text(safe(l), 20, y);
        doc.setTextColor(0);
        doc.setFont("helvetica", "bold");
        doc.text(safe(v), 190, y, { align: "right" });
        y += 10;
      };
      row("Name", userName);
      row("Mobile", mobileNumber);
      y += 3;
      doc.line(20, y, 190, y);
      y += 10;
      row("Transaction ID", selTxn.TransactionID);
      row("Order ID", selTxn.OrderID || selTxn.TransactionID);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(100);
      doc.text("Description", 20, y);
      doc.setTextColor(0);
      doc.setFont("helvetica", "bold");
      const desc = doc.splitTextToSize(safe(selTxn.TransactionRemarks), 120);
      doc.text(desc, 190, y, { align: "right" });
      y += desc.length * 6 + 4;
      const isCredit = selTxn.Credit > 0;
      row(
        isCredit ? "Amount (Credited)" : "Amount (Debited)",
        "Rs. " +
          Number(isCredit ? selTxn.Credit : selTxn.Debit || 0).toFixed(2),
      );
      row("Transaction Type", selTxn.TransactionType);
      row("Date", fmtDT(selTxn.TrxnDate));
      y += 5;
      doc.line(20, y, 190, y);
      y += 12;
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text("Customer Care: +91 9390358339", pw / 2, y, { align: "center" });
      y += 6;
      doc.text("support@taipl.in", pw / 2, y, { align: "center" });
      y += 10;
      doc.setFontSize(9);
      doc.text("Thank you for using our service", pw / 2, y, {
        align: "center",
      });
      doc.save(`Receipt_${safe(selTxn.TransactionID)}.pdf`);
    };
  };

  // ── Report Issue ────────────────────────────────────────────────────────────
  const openIssueDialog = () => {
    setIssueType("");
    setIssueDesc("");
    setIssueResult(null);
    setIssueLoading(false);
    setOpenIssue(true);
  };

  const issueServiceVal =
    [
      selTxn?.TransactionType,
      selTxn?.TransactionRemarks,
      selTxn?.ServiceName,
      selTxn?.ServiceType,
      selTxn?.TransactionID,
    ].find((v) => v != null && String(v).trim() !== "") || "Wallet Transaction";

  const submitIssue = async () => {
    if (!issueType) {
      setIssueResult({ ok: false, message: "Please select an issue type." });
      return;
    }
    if (!issueDesc.trim()) {
      setIssueResult({ ok: false, message: "Please describe your issue." });
      return;
    }
    setIssueResult(null);
    setIssueLoading(true);

    // ✅ Use the SELECTED transaction's own OrderID for the whole ticket flow.
    // The complaint is tied to this specific transaction, so we send its
    // OrderID (falling back to its TransactionID) instead of generating a
    // new one. Both /raise and /raise/upload are driven by this same value.
    const txnOrderId = selTxn?.OrderID || selTxn?.TransactionID || "";

    try {
      // ── STEP 1: /raise — plain JSON, returns encrypted envelope ──────────
      const raiseRes = await api.post(`${baseapi}/raise`, {
        MobileNumber: mobileNumber || "",
        Name: userName || "",
        IssueService: issueServiceVal,
        IssueDescription: issueDesc.trim(),
        DeviceType: "WEB",
        FcmToken: "",
        SessionID,
        EntityID: entity_id,
        MachineID: ip,
        Platform,
        GeoLocation: location,
        OrderId: txnOrderId,
        osVersion: "Windows 11",
      }, {
        headers: { "Content-Type": "application/json", Accept: "application/json" },
      });


      const { Encrypted_data, Platform: encPlatform, Encrypted_skey } =
        raiseRes.data || {};

      // ── STEP 2: /raise/upload — multipart, always called ─────────────────
      // Sends the encrypted envelope back + optional file attachment.
      // The actual ticket creation and response (TicketId, filePath, etc.)
      // comes from this second call, not from Step 1.
      const uploadForm = new FormData();
      uploadForm.append("encryptedData", Encrypted_data);
      uploadForm.append("platform", encPlatform);
      uploadForm.append("encryptedSkey", Encrypted_skey);
      if (selectedFile) {
        uploadForm.append("file", selectedFile);
      }

      const uploadRes = await api.post(`${baseapi}/raise/upload`, uploadForm, {
        headers: { Accept: "application/json" },
      });

      const d = uploadRes.data;
      const ok =
        d?.StatusCode === "1000" ||
        d?.StatusCode === 1000 ||
        d?.StatusCode === "10200" ||
        d?.StatusCode === 10200;

      setIssueResult({
        ok,
        ticketId: d?.TicketId || d?.Data?.ticketId,
        message:
          d?.Message ||
          (ok ? "Ticket raised successfully." : "Failed to raise complaint."),
      });

      if (ok) {
        handleRemoveFile(); // clear file after success
      }
    } catch {
      setIssueResult({
        ok: false,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setIssueLoading(false);
    }
  };

  // ── Report Fraud ────────────────────────────────────────────────────────────
  const openFraudDialog = () => {
    setFraudNature("");
    setFraudDate("");
    setFraudDesc("");
    setFraudResult(null);
    setFraudErr("");
    setFraudLoading(false);
    setOpenFraud(true);
  };

  const submitFraud = async () => {
    if (!fraudNature) {
      setFraudErr("Please select nature of fraud.");
      return;
    }
    if (!fraudDesc.trim()) {
      setFraudErr("Please enter fraud description.");
      return;
    }
    setFraudErr("");
    setFraudLoading(true);
    try {
      const d = new Date(fraudDate);
      const fmt = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
      const res = await api.post(`${baseapi}/grievancecpfi`, {
        ReqType: "Jan_Aadhaar_eWallet_GrevienceCPFIRDatainsert",
        Mobilenumber: mobileNumber || "",
        FraudReportedBy: "Wallet Holder",
        NatureofFraud: fraudNature,
        Customer_FraudOccurrencebyDate: fmt,
        Customer_FraudDetectedbyDate: fmt,
        Customer_Mobile_No: mobileNumber || "",
        OrderID: orderID,
        Remarks: fraudDesc.trim(),
        EntityName: "Jan Aadhaar eWallet",
        MachineID: ip,
        Platform,
        ApplicationType,
        EntityID: entity_id,
        SessionID,
        GeoLocation: location,
      });
      const ok = res?.data?.Response_Code === "1000";
      setFraudResult({
        ok,
        message: ok
          ? "Your issue has been filed successfully."
          : res?.data?.Message || "Something went wrong.",
      });
    } catch {
      setFraudResult({
        ok: false,
        message: "Something went wrong. Please try again.",
      });
    } finally {
      setFraudLoading(false);
    }
  };

  // ── Pagination ──────────────────────────────────────────────────────────────
  // FIX: Use a Set to guarantee no duplicate page numbers, then sort and
  // insert "..." ellipses only where there are real gaps.
  const pageNums = (() => {
    const pages = new Set([1, totalPages]);
    for (
      let p = Math.max(1, page - 1);
      p <= Math.min(totalPages, page + 1);
      p++
    ) {
      pages.add(p);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    return sorted.reduce((acc, p, i, arr) => {
      if (i > 0 && p - arr[i - 1] > 1) acc.push("...");
      acc.push(p);
      return acc;
    }, []);
  })();

  const btnBase = { textTransform: "none", borderRadius: 2, fontSize: 12 };









  // ── UI ───────────────────────────────────────────────────────────────────────
  return (
    <>
      <DashboardHeader />
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          bgcolor: "#f5f7fb",
          py: 4,
          px: { xs: 2, sm: 3, md: 6 },
        }}
      >
        <Box sx={{ maxWidth: 1200, mx: "auto" }}>
          {/* Header */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={3}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ color: "#111827", letterSpacing: "-0.3px" }}
              >
                Transaction History
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.3 }}>
                View and manage all your wallet transactions
              </Typography>
            </Box>
            <IconButton
              onClick={() => navigate("/dashboard")}
              sx={{
                border: "1.5px solid #e5e7eb",
                bgcolor: "#fff",
                "&:hover": { bgcolor: "#f9fafb" },
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" sx={{ color: "#374151" }} />
            </IconButton>
          </Box>

          {/* Summary Cards */}
          
          
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4,1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {[
              {
                label: "Total",
                value: filteredData.length,
                color: "#3b82f6",
                bg: "#eff6ff",
                icon: "📋",
              },
              {
                label: "Successful",
            
                value: countOf("SUCCESS"),
                color: "#16a34a",
                bg: "#f0fdf4",
                icon: "✅",
              },
              {
                label: "Failed",
                value: countOf("FAILURE"),
                color: "#dc2626",
                bg: "#fef2f2",
                icon: "❌",
              },
              {
                label: "Pending",
                value: countOf("PENDING"),
                color: "#d97706",
                bg: "#fffbeb",
                icon: "⏳",
              },
            ].map((c) => (
              <Paper
                key={c.label}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  bgcolor: c.bg,
                  border: `1.5px solid ${c.color}22`,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
                  }}
                >
                  {c.icon}
                </Box>
                <Box>
                  <Typography
                    fontSize={22}
                    fontWeight={700}
                    sx={{ color: c.color, lineHeight: 1.1 }}
                  >
                    {c.value}
                  </Typography>
                  <Typography
                    fontSize={11}
                    sx={{ color: "#6b7280", fontWeight: 500, mt: 0.2 }}
                  >
                    {c.label}
                  </Typography>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* Filter Card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              p: { xs: 2, md: 2.5 },
              mb: 2.5,
              bgcolor: "#fff",
              border: "1px solid #e5e7eb",
              boxShadow: "0 2px 8px rgba(15,23,42,0.05)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                justifyContent: "space-between",
                alignItems: { md: "flex-end" },
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "flex-end",
                }}
              >
                {[
                  { label: "FROM DATE", val: fromDate, set: setFromDate },
                  { label: "TO DATE", val: toDate, set: setToDate },
                ].map(({ label, val, set }) => (
                  <Box
                    key={label}
                    sx={{ display: "flex", flexDirection: "column" }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: "#9ca3af",
                        mb: 0.5,
                        fontSize: 10,
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </Typography>
                    <TextField
                      type="date"
                      value={val}
                      onChange={(e) => set(e.target.value)}
                      size="small"
                      sx={{
                        width: 155,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 2,
                          bgcolor: "#f9fafb",
                          fontSize: 13,
                          "& fieldset": { borderColor: "#e5e7eb" },
                        },
                      }}
                    />
                  </Box>
                ))}
                <Button
                  variant="contained"
                  onClick={() => setApplyFilter((p) => !p)}
                  sx={{
                    height: 40,
                    borderRadius: 2.5,
                    ...btnBase,
                    bgcolor: "#16a34a",
                    "&:hover": { bgcolor: "#15803d" },
                    px: 3.5,
                    fontWeight: 600,
                    boxShadow: "0 2px 8px rgba(22,163,74,0.25)",
                  }}
                >
                  Apply Filter
                </Button>
              </Box>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                  onClick={exportExcel}
                  sx={{
                    ...btnBase,
                    borderColor: "#d1d5db",
                    color: "#374151",
                    "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
                  }}
                >
                  Excel
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    <PictureAsPdfIcon sx={{ color: "#dc2626", fontSize: 16 }} />
                  }
                  onClick={exportPDF}
                  sx={{
                    ...btnBase,
                    borderColor: "#fca5a5",
                    color: "#dc2626",
                    "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" },
                  }}
                >
                  PDF
                </Button>
              </Box>
            </Box>
            <Typography
              variant="caption"
              sx={{ mt: 1.5, color: "#9ca3af", display: "block", fontSize: 11 }}
            >
              ⓘ Only 12 months of data available. For more data, contact
              support.
            </Typography>
          </Paper>

          {/* Search + Filter Bar */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: "100%", md: "380px" },
                display: "flex",
                alignItems: "center",
                borderRadius: 3,
                bgcolor: "#fff",
                border: "1px solid #e5e7eb",
                px: 2,
                py: 0.3,
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              }}
            >
              <SearchIcon sx={{ color: "#9ca3af", mr: 1, fontSize: 20 }} />
              <TextField
                placeholder="Search by transaction ID, description, date (e.g. 14-06-2026)"
                variant="standard"
                value={searchText}
                onChange={handleSearch}
                fullWidth
                InputProps={{ disableUnderline: true, sx: { fontSize: 13.5 } }}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <Select
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  minWidth: 150,
                  borderRadius: 2,
                  fontSize: 13,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#e5e7eb",
                  },
                }}
              >
                {["ALL", "SUCCESS", "FAILURE", "PENDING"].map((s) => (
                  <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>
                    {s === "ALL"
                      ? "All Status"
                      : s.charAt(0) + s.slice(1).toLowerCase()}
                  </MenuItem>
                ))}
              </Select>
              {[
                { l: "Success", s: "SUCCESS", bg: "#dcfce7", c: "#16a34a" },
                { l: "Failed", s: "FAILURE", bg: "#fee2e2", c: "#dc2626" },
                { l: "Pending", s: "PENDING", bg: "#fef3c7", c: "#d97706" },
              ].map(({ l, s, bg, c }) => (
                <Box
                  key={s}
                  sx={{
                    bgcolor: bg,
                    color: c,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {l}: {countOf(s)}
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Table ── */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              overflow: "hidden",
              bgcolor: "#fff",
              border: "1px solid #e5e7eb",
              boxShadow: "0 4px 16px rgba(15,23,42,0.07)",
            }}
          >
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table
                sx={{
                  minWidth: 700,
                  "& .MuiTableCell-head": {
                    fontWeight: 700,
                    fontSize: 11,
                    color: "#9ca3af",
                    letterSpacing: "0.5px",
                    borderBottomColor: "#f3f4f6",
                    backgroundColor: "#f9fafb",
                    textTransform: "uppercase",
                    py: 1.5,
                    px: 1.5,
                  },
                  "& .MuiTableCell-body": {
                    fontSize: 13.5,
                    borderBottomColor: "#f9fafb",
                    py: 1.2,
                    px: 1.5,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {[
                      "S.No",
                      "Date",
                      "Transaction ID / OrderID",
                      "Description",
                      "Credit",
                      "Debit",
                      "Status",
                      "Balance",
                    ].map((h, i) => (
                      <TableCell
                        key={h}
                        align={i >= 4 ? "right" : i === 6 ? "center" : "left"}
                        sx={{
                          width:
                            i === 0
                              ? "40px"
                              : i === 1
                                ? "120px"
                                : i === 2
                                  ? "150px"
                                  : i === 3
                                    ? "220px"
                                    : i === 4
                                      ? "80px"
                                      : i === 5
                                        ? "70px"
                                        : i === 6
                                          ? "90px"
                                          : "90px",
                          whiteSpace: "nowrap",
                          px: 1.5,
                        }}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statusFiltered.length > 0 ? (
                    paginated.map((row, i) => {
                      const cr = Number(row.Credit || 0),
                        dr = Number(row.Debit || 0),
                        bal = Number(row.Balance || 0);
                      return (
                        <TableRow
                          key={i}
                          hover
                          onClick={() => {
                            setSelTxn(row);
                            setOpenDetail(true);
                          }}
                          sx={{
                            cursor: "pointer",
                            "&:hover": { bgcolor: "#f8faff" },
                            "&:nth-of-type(even)": { bgcolor: "#fafafa" },
                          }}
                        >
                          <TableCell sx={{ px: 1.5 }}>
                            <Typography
                              fontSize={12}
                              color="text.secondary"
                              fontWeight={500}
                            >
                              {(page - 1) * ROWS + i + 1}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 1.5 }}>
                            <Typography
                              fontSize={13}
                              fontWeight={600}
                              color="#374151"
                            >
                              {fmtDT(row.TrxnDate)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ px: 1.5 }}>
                            <Typography
                              fontSize={12.5}
                              fontWeight={500}
                              sx={{
                                color: "#3b82f6",
                                fontFamily: "monospace",
                                bgcolor: "#eff6ff",
                                px: 1,
                                py: 0.3,
                                borderRadius: 1,
                                display: "inline-block",
                              }}
                            >
                              {row.TransactionID || row.OrderID || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              maxWidth: 220,
                              whiteSpace: "normal",
                              px: 1.5,
                            }}
                          >
                            <Typography
                              fontSize={13}
                              color="#4b5563"
                              lineHeight={1.4}
                            >
                              {row.TransactionRemarks || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ px: 1.5 }}>
                            {cr > 0 ? (
                              <Typography
                                fontSize={13.5}
                                fontWeight={700}
                                color="#16a34a"
                              >
                                +₹{cr.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography fontSize={13} color="#d1d5db">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ px: 1.5 }}>
                            {dr > 0 ? (
                              <Typography
                                fontSize={13.5}
                                fontWeight={700}
                                color="#dc2626"
                              >
                                -₹{dr.toFixed(2)}
                              </Typography>
                            ) : (
                              <Typography fontSize={13} color="#d1d5db">
                                —
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ px: 1.5 }}>
                            <StatusBadge status={row.TrxnStatus} />
                          </TableCell>
                          <TableCell align="right" sx={{ px: 1.5 }}>
                            <Typography
                              fontSize={13.5}
                              fontWeight={600}
                              color="#111827"
                            >
                              ₹{bal ? bal.toFixed(2) : "0.00"}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                        <Typography fontSize={32}>📭</Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#9ca3af", fontWeight: 500 }}
                        >
                          No transactions found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>

              {/* Pagination */}
              {statusFiltered.length > ROWS && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 2.5,
                    py: 2,
                    borderTop: "1px solid #f3f4f6",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: "#9ca3af", fontSize: 12 }}
                  >
                    Showing{" "}
                    <strong style={{ color: "#374151" }}>
                      {(page - 1) * ROWS + 1}–
                      {Math.min(page * ROWS, statusFiltered.length)}
                    </strong>{" "}
                    of{" "}
                    <strong style={{ color: "#374151" }}>
                      {statusFiltered.length}
                    </strong>
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={page === 1}
                      onClick={() => setPage((p) => p - 1)}
                      sx={{
                        ...btnBase,
                        borderColor: "#e5e7eb",
                        color: "#374151",
                      }}
                    >
                      ← Prev
                    </Button>
                    {pageNums.map((p, i) =>
                      p === "..." ? (
                        <Typography
                          key={`ellipsis-${i}`}
                          fontSize={12}
                          sx={{ color: "#9ca3af", px: 0.5 }}
                        >
                          ...
                        </Typography>
                      ) : (
                        <Button
                          key={p}
                          size="small"
                          variant={p === page ? "contained" : "outlined"}
                          onClick={() => setPage(p)}
                          sx={{
                            minWidth: 32,
                            height: 32,
                            p: 0,
                            borderRadius: 2,
                            fontSize: 12,
                            ...(p === page
                              ? {
                                  bgcolor: "#16a34a",
                                  borderColor: "#16a34a",
                                  color: "#fff",
                                  "&:hover": { bgcolor: "#15803d" },
                                }
                              : { borderColor: "#e5e7eb", color: "#374151" }),
                          }}
                        >
                          {p}
                        </Button>
                      ),
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={page === totalPages}
                      onClick={() => setPage((p) => p + 1)}
                      sx={{
                        ...btnBase,
                        borderColor: "#e5e7eb",
                        color: "#374151",
                      }}
                    >
                      Next →
                    </Button>
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ── Transaction Detail Dialog ── */}
      <Dialog
        open={openDetail}
        onClose={() => setOpenDetail(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 4, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          <Box textAlign="center" mb={2.5}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                bgcolor: statusStyle(selTxn?.TrxnStatus).bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 1.5,
                border: `2px solid ${statusStyle(selTxn?.TrxnStatus).color}44`,
                fontSize: 24,
              }}
            >
              {normStatus(selTxn?.TrxnStatus) === "SUCCESS"
                ? "✅"
                : normStatus(selTxn?.TrxnStatus) === "FAILURE"
                  ? "❌"
                  : "⏳"}
            </Box>
            <Typography fontSize={16} fontWeight={700} color="#111827">
              Transaction Details
            </Typography>
            <StatusBadge status={selTxn?.TrxnStatus} sx={{ mt: 0.8 }} />
          </Box>

          {/* Amount */}
          <Box
            sx={{
              textAlign: "center",
              mb: 2.5,
              py: 2,
              bgcolor: (selTxn?.Credit || 0) > 0 ? "#f0fdf4" : "#fef2f2",
              borderRadius: 3,
              border: `1px solid ${(selTxn?.Credit || 0) > 0 ? "#bbf7d0" : "#fecaca"}`,
            }}
          >
            <Typography
              fontSize={28}
              fontWeight={800}
              color={(selTxn?.Credit || 0) > 0 ? "#16a34a" : "#dc2626"}
              letterSpacing="-0.5px"
            >
              {(selTxn?.Credit || 0) > 0 ? "+" : "-"}₹
              {(selTxn?.Credit || 0) > 0 ? selTxn?.Credit : selTxn?.Debit}
            </Typography>
            <Typography fontSize={12} color="text.secondary" mt={0.3}>
              {(selTxn?.Credit || 0) > 0 ? "Amount Credited" : "Amount Debited"}
            </Typography>
          </Box>

          <Box
            sx={{
              bgcolor: "#f9fafb",
              borderRadius: 2.5,
              p: 2,
              mb: 2.5,
              border: "1px solid #f3f4f6",
            }}
          >
            <DetailRow
              label="Transaction ID"
              value={selTxn?.TransactionID || selTxn?.OrderID}
            />
            <DetailRow label="Description" value={selTxn?.TransactionRemarks} />
            <DetailRow label="Date & Time" value={fmtDT(selTxn?.TrxnDate)} />
            <DetailRow label="Balance" value={`₹ ${selTxn?.Balance}`} />
          </Box>

          <Box display="flex" flexDirection="column" gap={1.5}>
            <Button
              variant="contained"
              fullWidth
              onClick={downloadReceipt}
              sx={{
                borderRadius: 2.5,
                ...btnBase,
                bgcolor: "#111827",
                color: "#fff",
                fontWeight: 600,
                py: 1.2,
                "&:hover": { bgcolor: "#1f2937" },
              }}
            >
              Download Receipt
            </Button>
            <Box display="flex" gap={1.5}>
              <Button
                fullWidth
                variant="outlined"
                onClick={openFraudDialog}
                sx={{
                  borderRadius: 2.5,
                  ...btnBase,
                  borderColor: "#fca5a5",
                  color: "#dc2626",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#dc2626", bgcolor: "#fef2f2" },
                }}
              >
                Report Fraud
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={openIssueDialog}
                sx={{
                  borderRadius: 2.5,
                  ...btnBase,
                  borderColor: "#d1d5db",
                  color: "#374151",
                  fontWeight: 600,
                  "&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
                }}
              >
                Report Issue
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ── Report Issue Dialog ── */}
      <Dialog
        open={openIssue}
        onClose={() => {
          if (!issueLoading) {
            setOpenIssue(false);
            setIssueResult(null);
            handleRemoveFile();
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 4, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
        }}>
        <DialogContent sx={{ p: 3 }}>
          {issueResult?.ok ? (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: "#f0fdf4",
                  border: "3px solid #86efac",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2.5,
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 38, color: "#16a34a" }}
                />
              </Box>
              <Typography fontSize={16} fontWeight={800} color="#15803d" mb={1}>
                Ticket Raised Successfully!
              </Typography>
              <Typography
                fontSize={13}
                color="#6b7280"
                mb={2.5}
                lineHeight={1.6}
              >
                {issueResult.message}
              </Typography>
              {issueResult.ticketId && (
                <Box
                  sx={{
                    bgcolor: "#f0fdf4",
                    border: "1.5px dashed #86efac",
                    borderRadius: 3,
                    px: 2.5,
                    py: 2,
                    mb: 3,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography
                    fontSize={11}
                    fontWeight={700}
                    color="#6b7280"
                    letterSpacing="1px"
                  >
                    YOUR TICKET ID
                  </Typography>
                  <Typography
                    fontSize={20}
                    fontWeight={800}
                    color="#16a34a"
                    sx={{ fontFamily: "monospace", letterSpacing: "1px" }}
                  >
                    #{issueResult.ticketId}
                  </Typography>
                  <Typography fontSize={11} color="#9ca3af" mt={0.5}>
                    Save this ID to track your complaint
                  </Typography>
                </Box>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  setOpenIssue(false);
                  setIssueResult(null);
                }}
                sx={{
                  borderRadius: 2.5,
                  ...btnBase,
                  bgcolor: "#16a34a",
                  color: "#fff",
                  fontWeight: 700,
                  py: 1.3,
                  fontSize: 14,
                  "&:hover": { bgcolor: "#15803d" },
                }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Box textAlign="center" mb={2}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    bgcolor: "#fef3c7",
                    border: "2px solid #fde68a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: 1.5,
                    fontSize: 22,
                  }}
                >
                  ⚠️
                </Box>
                <Typography fontSize={16} fontWeight={700} color="#111827">
                  Report an Issue
                </Typography>
                <Typography fontSize={12} color="#6b7280" mt={0.3}>
                  Tell us what went wrong with this transaction
                </Typography>
              </Box>

              <Box
                sx={{
                  bgcolor: "#f9fafb",
                  borderRadius: 2.5,
                  p: 1.8,
                  mb: 2,
                  border: "1px solid #e5e7eb",
                }}
              >
                <Box display="flex" justifyContent="space-between" mb={0.8}>
                  <Typography fontSize={12} color="text.secondary">
                    Order ID
                  </Typography>
                  <Typography
                    fontSize={12}
                    fontWeight={600}
                    sx={{
                      fontFamily: "monospace",
                      bgcolor: "#e5e7eb",
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                    }}
                  >
                    {selTxn?.OrderID || selTxn?.TransactionID || "-"}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography fontSize={12} color="text.secondary">
                    Date & Time
                  </Typography>
                  <Typography fontSize={12} fontWeight={600}>
                    {fmtDT(selTxn?.TrxnDate)}
                  </Typography>
                </Box>
              </Box>

              {issueResult && !issueResult.ok && (
                <Alert
                  severity="error"
                  sx={{ mb: 2, borderRadius: 2, fontSize: 12 }}
                >
                  {issueResult.message}
                </Alert>
              )}

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel sx={{ fontSize: 13 }}>Select Issue Type</InputLabel>
                <Select
                  value={issueType}
                  label="Select Issue Type"
                  onChange={(e) => {
                    setIssueType(e.target.value);
                    setIssueResult(null);
                  }}
                  sx={{ borderRadius: 2, fontSize: 13 }}
                >
                  {[
                    "Load money amount is not credited",
                    "Credited to wrong beneficiary",
                    "Amount debited from wallet but transaction failed",
                    "Refund not received yet",
                    "Others",
                  ].map((o) => (
                    <MenuItem key={o} value={o} sx={{ fontSize: 13 }}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describe your issue"
                placeholder="Please provide details..."
                value={issueDesc}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setIssueDesc(e.target.value);
                    setIssueResult(null);
                  }
                }}
                size="small"
                inputProps={{ maxLength: 300 }}
                helperText={
                  <span style={{ color: "#6b7280" }}>
                    {issueDesc.length}/400 characters
                  </span>
                }
                sx={{
                  mb: 2,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    fontSize: 13,
                    "& fieldset": { borderColor: "#d1d5db" },
                    "&.Mui-focused fieldset": {
                      borderColor: "#16a34a",
                      borderWidth: "2px",
                    },
                  },
                }}
              />

              {/* ── File Attachment ── */}
              <Box mb={2.5}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", mb: 0.8 }}>
                  Attachment{" "}
                  <Typography component="span" sx={{ fontSize: 11, color: "#9ca3af", fontWeight: 400 }}>
                    (optional — PNG, JPEG or PDF, max 5MB)
                  </Typography>
                </Typography>

                {!selectedFile ? (
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<AttachFileIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      textTransform: "none",
                      borderRadius: 2,
                      borderColor: "#d1d5db",
                      color: "#6b7280",
                      fontSize: 12,
                      py: 0.8,
                      "&:hover": { borderColor: "#16a34a", color: "#16a34a" },
                    }}
                  >
                    Choose file
                    <input
                      type="file"
                      hidden
                      accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                      onChange={handleFileSelect}
                    />
                  </Button>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.2,
                      p: 1.2,
                      borderRadius: 2,
                      border: "1px solid #bbf7d0",
                      background: "#f0fdf4",
                    }}
                  >
                    {filePreviewUrl ? (
                      <Box
                        component="img"
                        src={filePreviewUrl}
                        alt="preview"
                        sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1.5, flexShrink: 0 }}
                      />
                    ) : (
                      <InsertDriveFileOutlinedIcon sx={{ fontSize: 28, color: "#16a34a", flexShrink: 0 }} />
                    )}
                    <Box sx={{ overflow: "hidden", flex: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#15803d", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {selectedFile.name}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#6b7280" }}>
                        {selectedFile.size < 1024 * 1024
                          ? `${(selectedFile.size / 1024).toFixed(1)} KB`
                          : `${(selectedFile.size / (1024 * 1024)).toFixed(1)} MB`}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      onClick={handleRemoveFile}
                      sx={{ minWidth: 0, p: 0.5, color: "#9ca3af", "&:hover": { color: "#dc2626" } }}
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </Button>
                  </Box>
                )}
              </Box>

              <Box display="flex" flexDirection="column" gap={1.2}>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={issueLoading}
                  onClick={submitIssue}
                  sx={{
                    borderRadius: 2.5,
                    ...btnBase,
                    bgcolor: "#16a34a",
                    color: "#fff",
                    fontWeight: 700,
                    py: 1.2,
                    "&:hover": { bgcolor: "#15803d" },
                  }}
                >
                  {issueLoading ? (
                    <CircularProgress size={20} sx={{ color: "#fff" }} />
                  ) : (
                    "Submit Issue"
                  )}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  disabled={issueLoading}
                  onClick={() => {
                    setOpenIssue(false);
                    setIssueResult(null);
                  }}
                  sx={{
                    borderRadius: 2.5,
                    ...btnBase,
                    borderColor: "#e5e7eb",
                    color: "#6b7280",
                  }}
                >
                  Cancel
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Report Fraud Dialog ── */}
      <Dialog
        open={openFraud}
        onClose={() => {
          if (!fraudLoading) {
            setOpenFraud(false);
            setFraudErr("");
            setFraudResult(null);
          }
        }}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: { borderRadius: 4, boxShadow: "0 20px 60px rgba(0,0,0,0.15)" },
        }}
      >
        <DialogContent sx={{ p: 3 }}>
          {fraudResult ? (
            <Box textAlign="center" py={2}>
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  bgcolor: fraudResult.ok ? "#f0fdf4" : "#fef2f2",
                  border: `3px solid ${fraudResult.ok ? "#86efac" : "#fca5a5"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  fontSize: 34,
                }}
              >
                {fraudResult.ok ? "✅" : "❌"}
              </Box>
              <Typography
                fontSize={18}
                fontWeight={800}
                color={fraudResult.ok ? "#15803d" : "#dc2626"}
                mb={1}
              >
                {fraudResult.ok
                  ? "Fraud Report Submitted"
                  : "Submission Failed"}
              </Typography>
              <Typography fontSize={13} color="#6b7280" mb={3}>
                {fraudResult.message}
              </Typography>
              <Button
                fullWidth
                variant="contained"
                onClick={() => {
                  setOpenFraud(false);
                  setFraudResult(null);
                }}
                sx={{
                  borderRadius: 2.5,
                  ...btnBase,
                  bgcolor: fraudResult.ok ? "#16a34a" : "#dc2626",
                }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Box textAlign="center" mb={2}>
                <Typography fontSize={18} fontWeight={700}>
                  Report Fraud
                </Typography>
                <Typography fontSize={12} color="#6b7280">
                  Submit fraud details for this transaction
                </Typography>
              </Box>

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Nature of Fraud</InputLabel>
                <Select
                  value={fraudNature}
                  label="Nature of Fraud"
                  onChange={(e) => {
                    setFraudNature(e.target.value);
                    setFraudErr("");
                  }}
                >
                  {[
                    "Account Hacking/Compromise/ Identity theft",
                    "Phishing",
                    "Remote Capture of Device",
                    "Lost/Stolen Device/ Instrument",
                    "card Skimming",
                    "Vishing",
                    "Smishing",
                    "SIM Swap",
                    "Website CLoning/ Fraudulent Link",
                    "Fraudulent App",
                    "Email Hacking/ Compromise",
                    "Forgery/ Modification of payment",
                    "Merchant Collusion",
                    "Collect Payment Request",
                  ].map((o) => (
                    <MenuItem key={o} value={o}>
                      {o}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={4}
                label="Description of Fraud"
                value={fraudDesc}
                onChange={(e) => {
                  if (e.target.value.length <= 250) {
                    setFraudDesc(e.target.value);
                    setFraudErr("");
                  }
                }}
                helperText={`${fraudDesc.length}/250`}
                size="small"
                sx={{ mb: 2 }}
              />

              {fraudErr && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {fraudErr}
                </Alert>
              )}

              <Button
                fullWidth
                variant="contained"
                onClick={submitFraud}
                disabled={fraudLoading}
                sx={{
                  borderRadius: 2.5,
                  ...btnBase,
                  bgcolor: "#dc2626",
                  "&:hover": { bgcolor: "#b91c1c" },
                  mb: 1.5,
                }}
              >
                {fraudLoading ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  "Report Fraud"
                )}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOpenFraud(false)}
                disabled={fraudLoading}
                sx={{ borderRadius: 2.5, ...btnBase }}
              >
                Cancel
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}